import {
    useEffect,
    useMemo,
    useState,
    useCallback,
    useLayoutEffect,
} from "react";
import { useNavigate } from "react-router-dom";
import {
    ClipboardList,
    AlertTriangle,
    CheckCircle2,
    Building2,
    Users,
    Wifi,
    Plus,
    MessageSquare,
    FolderOpen,
    Cloud,
    CloudRain,
    CloudSnow,
    CloudLightning,
    Sun,
    CloudFog,
    RotateCcw,
    Clock,
    CalendarDays,
    Calendar,
    X,
    Wind,
    Droplets,
    Thermometer,
    Mic,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    Legend,
} from "recharts";
import api from "../api/client";
import {
    formatBytes,
    formatUpcomingDate,
    getUpcomingRelativeDay,
} from "../lib/utils";

import { Card, Metric, Text, Flex } from "@tremor/react";
import { LoadingSpinner } from "../components/common/LoadingSpinner";
import { AppDownloadSection } from "../components/dashboard/AppDownloadSection";
import { DataCompletenessCard } from "../components/dashboard/DataCompletenessCard";
import { FieldPulseCard } from "../components/dashboard/FieldPulseCard";
import { MaliSummaryCard } from "../components/dashboard/MaliSummaryCard";
import { MaliNewsCard } from "../components/dashboard/MaliNewsCard";
import { AiExecutiveSummaryCard } from "../components/dashboard/AiExecutiveSummaryCard";
import { ReleaseNotesCard } from "../components/dashboard/ReleaseNotesCard";
import { UpcomingChecksCard } from "../components/dashboard/UpcomingChecksCard";
import { useProject } from "../hooks/useProject";
import { useAuth } from "../hooks/useAuth";
import { hasPermission } from "../lib/permissions";
import type {
    WeatherCache,
    UpcomingItem,
    DetailedWeather,
    WeatherPeriod,
} from "../types";

const STATUS_COLORS: Record<string, string> = {
    atandi: "#3b82f6",
    beklemede: "#f59e0b",
    yapiliyor: "#eab308",
    tamamlandi: "#22c55e",
    iptal_edildi: "#ef4444",
};

function useChartContainerSize<T extends HTMLElement>() {
    const [size, setSize] = useState({ width: 0, height: 0 });
    const [element, setElement] = useState<T | null>(null);

    const ref = useCallback((node: T | null) => {
        if (node) {
            setElement(node);
        }
    }, []);

    useLayoutEffect(() => {
        if (!element) return;

        const update = () => {
            const rect = element.getBoundingClientRect();
            setSize({ width: rect.width, height: rect.height });
        };

        update();

        const ro = new ResizeObserver(update);
        ro.observe(element);
        window.addEventListener("resize", update);
        return () => {
            ro.disconnect();
            window.removeEventListener("resize", update);
        };
    }, [element]);

    return { ref, size };
}

interface KPIData {
    total_tasks: number;
    overdue_tasks: number;
    completed_tasks: number;
    active_projects: number;
    total_users: number;
    online_users: number;
    storage_used: number;
    storage_limit: number;
}

interface ActivityItem {
    id: number;
    user: {
        id: number;
        name: string;
        avatar?: string | null;
    };
    action: string;
    created_at: string;
}

export default function DashboardPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [kpis, setKpis] = useState<KPIData | null>(null);
    const [statusData, setStatusData] = useState<
        { name: string; value: number; status: string }[]
    >([]);
    const [weeklyData, setWeeklyData] = useState<
        { name: string; tamamlanan: number }[]
    >([]);
    const [progressData, setProgressData] = useState<
        { project_name: string; percentage: number }[]
    >([]);
    const [activities, setActivities] = useState<ActivityItem[]>([]);
    const [upcoming, setUpcoming] = useState<UpcomingItem[]>([]);
    const [upcomingLoading, setUpcomingLoading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [fabOpen, setFabOpen] = useState(false);

    const statusChartContainer = useChartContainerSize<HTMLDivElement>();
    const weeklyChartContainer = useChartContainerSize<HTMLDivElement>();

    const { selectedProject } = useProject();
    const [weather, setWeather] = useState<WeatherCache | null>(null);
    const [weatherLoading, setWeatherLoading] = useState(false);
    const [weatherInfo, setWeatherInfo] = useState<string | null>(null);
    const [weatherDetailOpen, setWeatherDetailOpen] = useState(false);
    const [weatherDetail, setWeatherDetail] = useState<DetailedWeather | null>(
        null,
    );
    const [weatherDetailLoading, setWeatherDetailLoading] = useState(false);

    const mockWeather: WeatherCache = useMemo(
        () => ({
            id: 0,
            project_id: selectedProject?.id ?? 0,
            city_id: "0",
            city_name: selectedProject?.location || "İstanbul",
            temperature: 22,
            humidity: 65,
            wind_speed: 3.5,
            weather_main: "Clouds",
            weather_desc: "parçalı bulutlu",
            forecast_json: [],
            cached_at: new Date().toISOString(),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
        }),
        [selectedProject],
    );

    const storagePercent = useMemo(() => {
        if (!kpis || !kpis.storage_limit) return 0;
        return Math.min(
            100,
            Math.round((kpis.storage_used / kpis.storage_limit) * 100),
        );
    }, [kpis]);

    const fetchAll = useCallback(async (signal?: AbortSignal) => {
        setLoading(true);
        setError(null);
        try {
            const [kpiRes, statusRes, weeklyRes, progressRes, activityRes] =
                await Promise.all([
                    api.get("/api/v1/dashboard/kpis", { signal }),
                    api.get("/api/v1/dashboard/charts/status", { signal }),
                    api.get("/api/v1/dashboard/charts/weekly", { signal }),
                    api.get("/api/v1/dashboard/charts/progress", { signal }),
                    api.get("/api/v1/dashboard/activity", { signal }),
                ]);

            setUpcomingLoading(true);
            try {
                const upcomingRes = await api.get(
                    "/api/v1/dashboard/upcoming",
                    { signal },
                );
                setUpcoming((upcomingRes.data?.data || []) as UpcomingItem[]);
            } finally {
                setUpcomingLoading(false);
            }

            setKpis(kpiRes.data?.data || null);
            const statusPayload = statusRes.data as {
                labels: string[];
                data: number[];
                colors: string[];
                statuses: string[];
            };
            setStatusData(
                (statusPayload.statuses || []).map(
                    (status: string, idx: number) => ({
                        name: statusPayload.labels[idx] ?? status,
                        status,
                        value: statusPayload.data[idx] ?? 0,
                    }),
                ),
            );
            const weeklyPayload = weeklyRes.data as {
                day_tr: string;
                completed: number;
            }[];
            setWeeklyData(
                (weeklyPayload || []).map((item) => ({
                    name: item.day_tr,
                    tamamlanan: item.completed,
                })),
            );
            setProgressData(
                (progressRes.data as {
                    project_name: string;
                    percentage: number;
                }[]) || [],
            );
            setActivities(activityRes.data || []);
        } catch (err: unknown) {
            const e = err as { name?: string };
            if (e.name === "CanceledError" || e.name === "AbortError") {
                return;
            }
            console.error("Hata:", err);
            setError(
                "Veriler yüklenirken bir hata oluştu. Lütfen tekrar deneyin.",
            );
            setUpcomingLoading(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        fetchAll(controller.signal);
        return () => controller.abort();
    }, [fetchAll]);

    useEffect(() => {
        if (!selectedProject) {
            setWeather(null);
            setWeatherInfo(null);
            setWeatherLoading(false);
            return;
        }

        const controller = new AbortController();
        const fetchWeather = async () => {
            setWeatherLoading(true);
            setWeatherInfo(null);
            try {
                const res = await api.get(
                    `/api/v1/weather/${selectedProject.id}`,
                    { signal: controller.signal },
                );
                setWeather(res.data?.data || null);
            } catch (err: unknown) {
                const e = err as { name?: string };
                if (e.name === "CanceledError" || e.name === "AbortError") {
                    return;
                }
                console.error("Hava durumu hatasi:", err);
                setWeather(mockWeather);
                setWeatherInfo(
                    "Hava durumu servisine ulaşılamadı. Örnek veri gösteriliyor.",
                );
            } finally {
                setWeatherLoading(false);
            }
        };

        fetchWeather();
        return () => controller.abort();
    }, [selectedProject, mockWeather]);

    if (loading) {
        return (
            <div
                className="flex h-64 items-center justify-center"
                aria-live="polite"
                aria-busy="true"
            >
                <LoadingSpinner size="lg" />
            </div>
        );
    }

    const kpiCards = [
        {
            label: "Toplam Görev",
            value: kpis?.total_tasks ?? 0,
            icon: ClipboardList,
            color: "text-blue-600",
            bg: "bg-blue-50",
            testId: "kpi-total-tasks",
        },
        {
            label: "Geciken Görev",
            value: kpis?.overdue_tasks ?? 0,
            icon: AlertTriangle,
            color: "text-red-600",
            bg: "bg-red-50",
            testId: "kpi-delayed-tasks",
        },
        {
            label: "Bugün Tamamlanan",
            value: kpis?.completed_tasks ?? 0,
            icon: CheckCircle2,
            color: "text-green-600",
            bg: "bg-green-50",
            testId: "kpi-completed-tasks",
        },
        {
            label: "Aktif Şantiye",
            value: kpis?.active_projects ?? 0,
            icon: Building2,
            color: "text-indigo-600",
            bg: "bg-indigo-50",
            testId: "kpi-active-projects",
        },
        {
            label: "Toplam Kullanıcı",
            value: kpis?.total_users ?? 0,
            icon: Users,
            color: "text-slate-600",
            bg: "bg-slate-100",
            testId: "kpi-total-users",
        },
        {
            label: "Online Kullanıcı",
            value: kpis?.online_users ?? 0,
            icon: Wifi,
            color: "text-green-600",
            bg: "bg-green-50",
            testId: "kpi-online-users",
        },
    ];

    const quickActions: Array<{
        label: string;
        icon: LucideIcon;
        action: () => void;
        testId: string;
        permission?: { module: string; action: string };
    }> = [
        {
            label: "Yeni Görev",
            icon: ClipboardList,
            action: () => navigate("/tasks"),
            testId: "quick-action-task",
        },
        {
            label: "Yeni Dosya",
            icon: FolderOpen,
            action: () => navigate("/files"),
            testId: "quick-action-file",
        },
        {
            label: "Yeni Mesaj",
            icon: MessageSquare,
            action: () => navigate("/messages"),
            testId: "quick-action-message",
        },
        {
            label: "Yeni Proje",
            icon: Building2,
            action: () => navigate("/projects"),
            testId: "quick-action-project",
        },
        {
            label: "Sesli Not",
            icon: Mic,
            action: () => navigate("/voice-notes"),
            testId: "quick-action-voice-note",
            permission: { module: "voice_notes", action: "view" },
        },
    ].filter(
        (qa) =>
            !qa.permission ||
            hasPermission(user, qa.permission.module, qa.permission.action)
    );

    const getWeatherIcon = (main?: string | null) => {
        switch (main?.toLowerCase()) {
            case "clear":
                return (
                    <Sun
                        className="h-8 w-8 text-amber-500"
                        aria-hidden="true"
                    />
                );
            case "clouds":
                return (
                    <Cloud
                        className="h-8 w-8 text-slate-500"
                        aria-hidden="true"
                    />
                );
            case "rain":
            case "drizzle":
                return (
                    <CloudRain
                        className="h-8 w-8 text-blue-500"
                        aria-hidden="true"
                    />
                );
            case "snow":
                return (
                    <CloudSnow
                        className="h-8 w-8 text-sky-400"
                        aria-hidden="true"
                    />
                );
            case "thunderstorm":
                return (
                    <CloudLightning
                        className="h-8 w-8 text-purple-500"
                        aria-hidden="true"
                    />
                );
            case "mist":
            case "fog":
            case "haze":
                return (
                    <CloudFog
                        className="h-8 w-8 text-slate-500"
                        aria-hidden="true"
                    />
                );
            default:
                return (
                    <Cloud
                        className="h-8 w-8 text-slate-500"
                        aria-hidden="true"
                    />
                );
        }
    };

    const formatTimeAgo = (dateStr: string) => {
        const diff = Date.now() - new Date(dateStr).getTime();
        const minutes = Math.floor(diff / 60000);
        if (minutes < 1) return "az önce";
        if (minutes < 60) return `${minutes} dk önce`;
        const hours = Math.floor(minutes / 60);
        if (hours < 24) return `${hours} sa önce`;
        return `${Math.floor(hours / 24)} gün önce`;
    };

    const openWeatherDetail = async () => {
        if (!selectedProject) return;
        setWeatherDetailOpen(true);
        setWeatherDetailLoading(true);
        try {
            const res = await api.get(
                `/api/v1/weather/${selectedProject.id}/detailed`,
            );
            setWeatherDetail(res.data?.data || null);
        } catch (err) {
            console.error("Detaylı hava durumu hatası:", err);
        } finally {
            setWeatherDetailLoading(false);
        }
    };

    const formatPeriod = (p: WeatherPeriod) => {
        const date = p.date || p.datetime;
        const label = date
            ? new Date(date).toLocaleDateString("tr-TR", {
                  weekday: "short",
                  day: "numeric",
              })
            : "-";
        return { label, temp: p.temperature ?? p.temp_max ?? "-" };
    };

    return (
        <div data-testid="dashboard-page" className="space-y-6 pb-24 lg:pb-8">
            {error && (
                <div
                    data-testid="dashboard-error-container"
                    className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
                    role="alert"
                >
                    <AlertTriangle
                        className="h-5 w-5 shrink-0"
                        aria-hidden="true"
                    />
                    <span className="flex-1">{error}</span>
                    <button
                        type="button"
                        onClick={() => fetchAll()}
                        data-testid="dashboard-retry-button"
                        className="flex items-center gap-1 rounded-md bg-white px-2 py-1 text-xs font-semibold text-red-700 shadow-sm hover:bg-red-100"
                    >
                        <RotateCcw className="h-3 w-3" aria-hidden="true" />
                        Tekrar dene
                    </button>
                </div>
            )}

            {/* Gecikme Uyarıları */}
            {(kpis?.overdue_tasks ?? 0) > 0 && (
                <div
                    data-testid="overdue-warning-banner"
                    className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700"
                    role="alert"
                >
                    <AlertTriangle
                        className="h-5 w-5 shrink-0"
                        aria-hidden="true"
                    />
                    <span>
                        {kpis?.overdue_tasks} görev gecikti! Lütfen inceleyin.
                    </span>
                </div>
            )}

            {/* Mobil Uygulama İndirme */}
            <AppDownloadSection />

            {/* KPI Kartları */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {kpiCards.map((kpi) => {
                    const Icon = kpi.icon;
                    const decorationColor =
                        kpi.color === "text-blue-600"
                            ? "blue"
                            : kpi.color === "text-red-600"
                              ? "red"
                              : kpi.color === "text-green-600"
                                ? "green"
                                : kpi.color === "text-indigo-600"
                                  ? "indigo"
                                  : "slate";
                    return (
                        <Card
                            key={kpi.label}
                            data-testid={kpi.testId}
                            decoration="top"
                            decorationColor={decorationColor}
                            aria-label={`${kpi.label}: ${kpi.value}`}
                        >
                            <Flex
                                justifyContent="start"
                                alignItems="center"
                                className="gap-4"
                            >
                                <div
                                    className={`flex h-12 w-12 items-center justify-center rounded-lg ${kpi.bg}`}
                                >
                                    <Icon
                                        className={`h-6 w-6 ${kpi.color}`}
                                        aria-hidden="true"
                                    />
                                </div>
                                <div>
                                    <Text data-testid={`${kpi.testId}-label`}>
                                        {kpi.label}
                                    </Text>
                                    <Metric data-testid={`${kpi.testId}-value`}>
                                        {kpi.value}
                                    </Metric>
                                </div>
                            </Flex>
                        </Card>
                    );
                })}
            </div>

            {/* Kota Kullanımı */}
            <Card data-testid="storage-kpi-card">
                <Flex justifyContent="between" alignItems="center">
                    <Text>Kota Kullanımı</Text>
                    <Text data-testid="storage-percent-text">
                        {storagePercent}%
                    </Text>
                </Flex>
                <div
                    className="h-3 w-full overflow-hidden rounded-full bg-slate-100"
                    aria-label="Kota kullanım çubuğu"
                >
                    <div
                        data-testid="storage-usage-bar"
                        className={`h-full rounded-full transition-all ${
                            storagePercent >= 90
                                ? "bg-red-500"
                                : storagePercent >= 80
                                  ? "bg-orange-500"
                                  : "bg-blue-500"
                        }`}
                        style={{ width: `${storagePercent}%` }}
                    />
                </div>
                <Text data-testid="storage-usage-text">
                    {formatBytes(kpis?.storage_used ?? 0)} /{" "}
                    {formatBytes(kpis?.storage_limit ?? 0)}
                </Text>
            </Card>

            {/* Grafikler */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div className="rounded-xl bg-white p-5 shadow-sm min-w-0" data-testid="status-chart">
                    <h3 className="mb-4 text-sm font-semibold text-slate-900">
                        Görev Statü Dağılımı
                    </h3>
                    <div
                        ref={statusChartContainer.ref}
                        className="h-64 min-w-0"
                    >
                        {statusChartContainer.size.width === 0 ||
                        statusChartContainer.size.height === 0 ||
                        statusData.length === 0 ||
                        statusData.every((d) => d.value === 0) ? (
                            <div data-testid="status-chart-empty" className="flex h-full flex-col items-center justify-center text-sm text-slate-500">
                                <ClipboardList
                                    className="mb-2 h-8 w-8"
                                    aria-hidden="true"
                                />
                                Henüz görev verisi yok.
                            </div>
                        ) : (
                            <ResponsiveContainer
                                width={statusChartContainer.size.width}
                                height={statusChartContainer.size.height}
                            >
                                <PieChart>
                                    <Pie
                                        data={statusData}
                                        dataKey="value"
                                        nameKey="name"
                                        cx="50%"
                                        cy="50%"
                                        outerRadius="80%"
                                        label
                                    >
                                        {statusData.map((entry, index) => (
                                            <Cell
                                                key={`cell-${index}`}
                                                fill={
                                                    STATUS_COLORS[
                                                        entry.status
                                                    ] || "#94a3b8"
                                                }
                                            />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>

                <div className="rounded-xl bg-white p-5 shadow-sm min-w-0" data-testid="weekly-chart">
                    <h3 className="mb-4 text-sm font-semibold text-slate-900">
                        Haftalık Tamamlanan Görevler
                    </h3>
                    <div
                        ref={weeklyChartContainer.ref}
                        className="h-64 min-w-0"
                    >
                        {weeklyChartContainer.size.width === 0 ||
                        weeklyChartContainer.size.height === 0 ||
                        weeklyData.length === 0 ||
                        weeklyData.every((d) => d.tamamlanan === 0) ? (
                            <div data-testid="weekly-chart-empty" className="flex h-full flex-col items-center justify-center text-sm text-slate-500">
                                <CheckCircle2
                                    className="mb-2 h-8 w-8"
                                    aria-hidden="true"
                                />
                                Bu hafta tamamlanan görev yok.
                            </div>
                        ) : (
                            <ResponsiveContainer
                                width={weeklyChartContainer.size.width}
                                height={weeklyChartContainer.size.height}
                            >
                                <BarChart data={weeklyData}>
                                    <XAxis
                                        dataKey="name"
                                        tick={{ fontSize: 12 }}
                                    />
                                    <YAxis
                                        allowDecimals={false}
                                        tick={{ fontSize: 12 }}
                                    />
                                    <Tooltip />
                                    <Bar
                                        dataKey="tamamlanan"
                                        fill="#3b82f6"
                                        radius={[4, 4, 0, 0]}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>
            </div>

            {/* Proje İlerlemesi */}
            <div className="rounded-xl bg-white p-5 shadow-sm" data-testid="project-progress">
                <h3 className="mb-4 text-sm font-semibold text-slate-900">
                    Proje İlerlemesi
                </h3>
                {progressData.length === 0 ? (
                    <div data-testid="project-progress-empty" className="flex h-32 flex-col items-center justify-center text-sm text-slate-500">
                        <Building2
                            className="mb-2 h-8 w-8"
                            aria-hidden="true"
                        />
                        Henüz proje verisi yok.
                    </div>
                ) : (
                    <div className="space-y-3">
                        {progressData.slice(0, 8).map((p, index) => (
                            <div
                                key={p.project_name}
                                data-testid={`project-progress-row-${index}`}
                                className="flex items-center gap-3"
                            >
                                <span
                                    className="min-w-0 flex-1 truncate text-sm text-slate-700"
                                    title={p.project_name}
                                >
                                    {p.project_name}
                                </span>
                                <div className="h-2 w-24 flex-1 overflow-hidden rounded-full bg-slate-100 sm:w-40">
                                    <div
                                        data-testid={`project-progress-bar-${index}`}
                                        className={`h-full rounded-full ${p.percentage >= 100 ? "bg-green-500" : p.percentage >= 50 ? "bg-blue-500" : "bg-amber-500"}`}
                                        style={{
                                            width: `${Math.min(100, p.percentage)}%`,
                                        }}
                                    />
                                </div>
                                <span data-testid={`project-progress-percent-${index}`} className="w-10 text-right text-xs font-medium text-slate-600">
                                    {p.percentage}%
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Yaklaşan Görevler ve Etkinlikler */}
            <div className="rounded-xl bg-white p-5 shadow-sm">
                <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-slate-900">
                        Yaklaşan Görevler ve Etkinlikler
                    </h3>
                    <CalendarDays
                        className="h-5 w-5 text-slate-500"
                        aria-hidden="true"
                    />
                </div>
                {upcomingLoading ? (
                    <div className="flex h-32 items-center justify-center">
                        <LoadingSpinner size="sm" />
                    </div>
                ) : upcoming.length === 0 ? (
                    <div data-testid="upcoming-empty" className="flex flex-col items-center justify-center rounded-lg border border-dashed border-slate-200 py-8 text-sm text-slate-500">
                        <Calendar className="mb-2 h-8 w-8" aria-hidden="true" />
                        Yaklaşan görev veya etkinlik yok.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {upcoming.slice(0, 6).map((item) => {
                            const relative = getUpcomingRelativeDay(item.date);
                            const meta = item.meta || {};
                            return (
                                <button
                                    key={`${item.type}-${item.id}`}
                                    data-testid={`upcoming-item-${item.type}-${item.id}`}
                                    onClick={() => navigate(item.url)}
                                    className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-left transition hover:border-blue-300 hover:bg-blue-50"
                                >
                                    <div className="flex items-start gap-3">
                                        <div
                                            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                                                item.type === "task"
                                                    ? "bg-blue-100 text-blue-700"
                                                    : item.type === "event"
                                                      ? "bg-purple-100 text-purple-700"
                                                      : "bg-amber-100 text-amber-700"
                                            }`}
                                        >
                                            {item.type === "task" ? (
                                                <ClipboardList className="h-4 w-4" />
                                            ) : item.type === "event" ? (
                                                <Calendar className="h-4 w-4" />
                                            ) : (
                                                <Clock className="h-4 w-4" />
                                            )}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate text-sm font-medium text-slate-900">
                                                {item.title}
                                            </p>
                                            <p className="text-xs text-slate-500">
                                                {formatUpcomingDate(item.date)}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-1.5 pl-12">
                                        {relative && (
                                            <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
                                                {relative}
                                            </span>
                                        )}
                                        {item.type === "task" &&
                                            Boolean(meta.priority) && (
                                                <span
                                                    className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
                                                        meta.priority === "acil"
                                                            ? "bg-red-100 text-red-700"
                                                            : meta.priority ===
                                                                "yuksek"
                                                              ? "bg-orange-100 text-orange-700"
                                                              : meta.priority ===
                                                                  "orta"
                                                                ? "bg-yellow-100 text-yellow-700"
                                                                : "bg-slate-100 text-slate-600"
                                                    }`}
                                                >
                                                    {String(meta.priority)
                                                        .charAt(0)
                                                        .toUpperCase() +
                                                        String(
                                                            meta.priority,
                                                        ).slice(1)}
                                                </span>
                                            )}
                                        {Boolean(meta.project_name) && (
                                            <span className="truncate rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">
                                                {String(meta.project_name)}
                                            </span>
                                        )}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                {/* Aktivite Akışı */}
                <div className="rounded-xl bg-white p-5 shadow-sm lg:col-span-2" data-testid="activity-stream">
                    <h3 className="mb-4 text-sm font-semibold text-slate-900">
                        Son Aktiviteler
                    </h3>
                    <div className="space-y-3">
                        {activities.length === 0 && (
                            <p data-testid="activity-empty" className="text-sm text-slate-500">
                                Henüz aktivite yok.
                            </p>
                        )}
                        {activities.slice(0, 10).map((a) => (
                            <div key={a.id} data-testid={`activity-item-${a.id}`} className="flex items-center gap-3">
                                {a.user?.avatar ? (
                                    <img
                                        src={a.user.avatar}
                                        alt=""
                                        className="h-8 w-8 rounded-full object-cover"
                                    />
                                ) : (
                                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-xs font-semibold text-blue-700">
                                        {a.user?.name?.[0] || "?"}
                                    </div>
                                )}
                                <div className="min-w-0 flex-1">
                                    <p className="break-words text-sm text-slate-800">
                                        <span className="font-medium">
                                            {a.user?.name || "Kullanıcı"}
                                        </span>{" "}
                                        <span className="text-slate-500">
                                            {a.action}
                                        </span>
                                    </p>
                                </div>
                                <span className="text-xs text-slate-500">
                                    {formatTimeAgo(a.created_at)}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Hava Durumu Widget + Veri Tamlık Skoru */}
                <div className="flex flex-col gap-4">
                <div className="rounded-xl bg-white p-5 shadow-sm" data-testid="weather-widget">
                    <div className="mb-4 flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-slate-900">
                            Hava Durumu
                        </h3>
                        {weather && weather.temperature !== null && (
                            <button
                                onClick={() => openWeatherDetail()}
                                data-testid="weather-detail-button"
                                className="text-xs font-medium text-blue-600 hover:text-blue-700"
                            >
                                Detaylı
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-4">
                        {weatherLoading ? (
                            <LoadingSpinner size="sm" />
                        ) : weather && weather.temperature !== null ? (
                            <>
                                {getWeatherIcon(weather.weather_main)}
                                <div className="min-w-0 flex-1">
                                    <p className="text-2xl font-bold text-slate-900">
                                        {Math.round(weather.temperature)}°C
                                    </p>
                                    <p
                                        className="truncate text-sm text-slate-500"
                                        title={`${weather.city_name || selectedProject?.location || selectedProject?.name || "Bilinmeyen Konum"}${weather.weather_desc ? `, ${weather.weather_desc}` : ""}`}
                                    >
                                        {weather.city_name ||
                                            selectedProject?.location ||
                                            selectedProject?.name ||
                                            "Bilinmeyen Konum"}
                                        {weather.weather_desc
                                            ? `, ${weather.weather_desc}`
                                            : ""}
                                    </p>
                                </div>
                            </>
                        ) : (
                            <div data-testid="weather-select-project-message" className="text-sm text-slate-500">
                                Hava durumunu görmek için bir proje seçin.
                            </div>
                        )}
                    </div>
                    {weatherInfo && (
                        <p
                            data-testid="weather-info-text"
                            className="mt-2 text-xs text-amber-600"
                            role="status"
                        >
                            {weatherInfo}
                        </p>
                    )}
                </div>
                <DataCompletenessCard />
                <FieldPulseCard projectId={selectedProject?.id} />
                <MaliSummaryCard />
                <MaliNewsCard />
                {user && ["admin", "yonetici", "sef"].includes(user.role) && (
                    <AiExecutiveSummaryCard projectId={selectedProject?.id} />
                )}
                <UpcomingChecksCard />
                <ReleaseNotesCard />
                </div>
            </div>

            {/* Hızlı İşlemler FAB */}
            <div className="fixed bottom-20 right-4 z-40 lg:bottom-8">
                {fabOpen && (
                    <div className="mb-3 flex flex-col items-end gap-2">
                        {quickActions.map((qa) => {
                            const Icon = qa.icon;
                            return (
                                <button
                                    key={qa.label}
                                    type="button"
                                    data-testid={qa.testId}
                                    aria-label={qa.label}
                                    onClick={() => {
                                        qa.action();
                                        setFabOpen(false);
                                    }}
                                    className="flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-lg ring-1 ring-slate-200 transition hover:bg-slate-50"
                                >
                                    {qa.label}
                                    <Icon
                                        className="h-4 w-4"
                                        aria-hidden="true"
                                    />
                                </button>
                            );
                        })}
                    </div>
                )}
                <button
                    type="button"
                    data-testid="quick-action-fab"
                    aria-label="Hızlı işlemler"
                    aria-expanded={fabOpen}
                    onClick={() => setFabOpen(!fabOpen)}
                    className="flex h-14 w-14 items-center justify-center rounded-full bg-blue-600 text-white shadow-lg transition hover:bg-blue-700"
                >
                    <Plus
                        className={`h-6 w-6 transition-transform ${fabOpen ? "rotate-45" : ""}`}
                        aria-hidden="true"
                    />
                </button>
            </div>

            {/* Detaylı Hava Durumu Modal */}
            {weatherDetailOpen && (
                <div data-testid="weather-detail-modal" className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 pt-10">
                    <div className="w-full max-w-3xl rounded-xl bg-white p-6 shadow-xl">
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="text-lg font-bold text-slate-900">
                                Detaylı Hava Durumu
                            </h2>
                            <button
                                data-testid="weather-detail-close-button"
                                onClick={() => setWeatherDetailOpen(false)}
                                className="rounded-lg p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-600"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>

                        {weatherDetailLoading ? (
                            <div className="flex h-48 items-center justify-center">
                                <LoadingSpinner size="lg" />
                            </div>
                        ) : !weatherDetail ? (
                            <p data-testid="weather-detail-error" className="text-sm text-slate-500">
                                Hava durumu detayları yüklenemedi.
                            </p>
                        ) : (
                            <div className="space-y-6">
                                <div className="flex flex-col items-center gap-4 rounded-xl bg-gradient-to-r from-blue-500 to-blue-400 p-5 text-white sm:flex-row sm:items-center">
                                    {getWeatherIcon(
                                        weatherDetail.current.weather_main,
                                    )}
                                    <div className="min-w-0 flex-1 text-center sm:text-left">
                                        <p className="text-3xl font-bold">
                                            {Math.round(
                                                weatherDetail.current
                                                    .temperature,
                                            )}
                                            °C
                                        </p>
                                        <p className="truncate text-sm opacity-90">
                                            {weatherDetail.current.city_name} —{" "}
                                            {weatherDetail.current.weather_desc}
                                        </p>
                                    </div>
                                    <div className="grid w-full grid-cols-2 gap-4 text-sm sm:w-auto sm:ml-auto">
                                        <div data-testid="weather-detail-humidity" className="flex items-center gap-1">
                                            <Droplets className="h-4 w-4" />%
                                            {weatherDetail.current.humidity}
                                        </div>
                                        <div data-testid="weather-detail-wind" className="flex items-center gap-1">
                                            <Wind className="h-4 w-4" />
                                            {
                                                weatherDetail.current.wind_speed
                                            }{" "}
                                            m/s
                                        </div>
                                        <div data-testid="weather-detail-feels-like" className="flex items-center gap-1">
                                            <Thermometer className="h-4 w-4" />
                                            Hissedilen{" "}
                                            {Math.round(
                                                weatherDetail.current
                                                    .feels_like,
                                            )}
                                            °
                                        </div>
                                    </div>
                                </div>

                                {weatherDetail.daily.length > 0 && (
                                    <div>
                                        <h3 className="mb-3 text-sm font-semibold text-slate-900">
                                            Günlük / Haftalık Tahmin
                                        </h3>
                                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                                            {weatherDetail.daily
                                                .slice(0, 7)
                                                .map((p, idx) => {
                                                    const { label, temp } =
                                                        formatPeriod(p);
                                                    return (
                                                        <div
                                                            key={idx}
                                                            data-testid={`weather-daily-card-${idx}`}
                                                            className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-center"
                                                        >
                                                            <p className="text-xs text-slate-500">
                                                                {label}
                                                            </p>
                                                            <p className="text-lg font-bold text-slate-900">
                                                                {typeof temp ===
                                                                "number"
                                                                    ? `${Math.round(temp)}°`
                                                                    : temp}
                                                            </p>
                                                            {p.weather_desc && (
                                                                <p className="text-xs text-slate-500">
                                                                    {
                                                                        p.weather_desc
                                                                    }
                                                                </p>
                                                            )}
                                                            {p.temp_min !=
                                                                null &&
                                                                p.temp_max !=
                                                                    null && (
                                                                    <p className="text-xs text-slate-500">
                                                                        {Math.round(
                                                                            p.temp_min,
                                                                        )}
                                                                        ° /{" "}
                                                                        {Math.round(
                                                                            p.temp_max,
                                                                        )}
                                                                        °
                                                                    </p>
                                                                )}
                                                            {typeof p.rain_probability ===
                                                                "number" &&
                                                                p.rain_probability >
                                                                    0 && (
                                                                    <p className="mt-1 text-xs text-blue-500">
                                                                        %
                                                                        {
                                                                            p.rain_probability
                                                                        }{" "}
                                                                        yağış
                                                                    </p>
                                                                )}
                                                        </div>
                                                    );
                                                })}
                                        </div>
                                    </div>
                                )}

                                {weatherDetail.hourly.length > 0 && (
                                    <div>
                                        <h3 className="mb-3 text-sm font-semibold text-slate-900">
                                            Saatlik Tahmin
                                        </h3>
                                        <div className="flex gap-2 overflow-x-auto pb-2">
                                            {weatherDetail.hourly
                                                .slice(0, 24)
                                                .map((p, idx) => {
                                                    const time = p.datetime
                                                        ? new Date(
                                                              p.datetime,
                                                          ).toLocaleTimeString(
                                                              "tr-TR",
                                                              {
                                                                  hour: "2-digit",
                                                                  minute: "2-digit",
                                                              },
                                                          )
                                                        : `${idx}:00`;
                                                    return (
                                                        <div
                                                            key={idx}
                                                            data-testid={`weather-hourly-card-${idx}`}
                                                            className="min-w-[80px] rounded-lg border border-slate-200 bg-slate-50 p-2 text-center"
                                                        >
                                                            <p className="text-xs text-slate-500">
                                                                {time}
                                                            </p>
                                                            <p className="text-base font-bold text-slate-900">
                                                                {typeof p.temperature ===
                                                                "number"
                                                                    ? `${Math.round(p.temperature)}°`
                                                                    : "-"}
                                                            </p>
                                                            {typeof p.rain_probability ===
                                                                "number" &&
                                                                p.rain_probability >
                                                                    0 && (
                                                                    <p className="text-[10px] text-blue-500">
                                                                        %
                                                                        {
                                                                            p.rain_probability
                                                                        }
                                                                    </p>
                                                                )}
                                                        </div>
                                                    );
                                                })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
